import QRCode from 'qrcode';

// ============================================================
// Queue-specific ESC/POS printer
// USB ONLY
//
// Does NOT use:
// - bluetoothPrinter.ts
// - usbPrinter.ts
// - PrintJob
// - buildReceiptBytes()
// - browser window.print()
// - 127.0.0.1 print bridge
// ============================================================

const ESC = 0x1B;
const GS = 0x1D;

const CMD = {
  INIT: [ESC, 0x40],

  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_LEFT: [ESC, 0x61, 0x00],

  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],

  DOUBLE_HEIGHT: [ESC, 0x21, 0x10],
  NORMAL_SIZE: [ESC, 0x21, 0x00],

  FEED: [ESC, 0x64, 0x04],

  CUT: [GS, 0x56, 0x41, 0x10],
};

// ============================================================
// Text helpers
// ============================================================

function textToBytes(text: string): number[] {
  return Array.from(
    new TextEncoder().encode(text)
  );
}

function divider(width = 32): number[] {
  return textToBytes(
    '-'.repeat(width) + '\n'
  );
}

function centerText(text: string): number[] {
  return [
    ...CMD.ALIGN_CENTER,
    ...textToBytes(text + '\n'),
  ];
}

function leftText(text: string): number[] {
  return [
    ...CMD.ALIGN_LEFT,
    ...textToBytes(text + '\n'),
  ];
}

function row(
  label: string,
  value: string,
  width = 32
): number[] {
  const left = String(label || '');
  const right = String(value || '');

  const spaces = Math.max(
    1,
    width - left.length - right.length
  );

  return textToBytes(
    left +
      ' '.repeat(spaces) +
      right +
      '\n'
  );
}

// ============================================================
// QR → ESC/POS raster image
// ============================================================

function qrImage(
  data: string,
  moduleSize = 4
): number[] {
  const qr = QRCode.create(data, {
    errorCorrectionLevel: 'M',
  });

  const modules = qr.modules.size;
  const matrix = qr.modules.data;

  const quietZone = 4;

  const qrModules =
    modules + quietZone * 2;

  const width =
    qrModules * moduleSize;

  // ESC/POS raster images require
  // a complete number of bytes per row.
  const widthBytes =
    Math.ceil(width / 8);

  const height = width;

  const pixels = new Uint8Array(
    widthBytes * height
  );

  for (
    let y = 0;
    y < height;
    y++
  ) {
    const qrY =
      Math.floor(y / moduleSize) -
      quietZone;

    for (
      let x = 0;
      x < width;
      x++
    ) {
      const qrX =
        Math.floor(x / moduleSize) -
        quietZone;

      let black = false;

      if (
        qrX >= 0 &&
        qrX < modules &&
        qrY >= 0 &&
        qrY < modules
      ) {
        black =
          matrix[
            qrY * modules + qrX
          ] === 1;
      }

      if (black) {
        const byteIndex =
          y * widthBytes +
          Math.floor(x / 8);

        const bit =
          7 - (x % 8);

        pixels[byteIndex] |=
          1 << bit;
      }
    }
  }

  const xL =
    widthBytes & 0xff;

  const xH =
    (widthBytes >> 8) & 0xff;

  const yL =
    height & 0xff;

  const yH =
    (height >> 8) & 0xff;

  return [
    ...CMD.ALIGN_CENTER,

    // GS v 0
    GS,
    0x76,
    0x30,
    0x00,

    xL,
    xH,
    yL,
    yH,

    ...Array.from(pixels),

    ...CMD.ALIGN_CENTER,
  ];
}

// ============================================================
// Non-Latin text → ESC/POS raster image
//
// Thermal printers' built-in ESC/POS text mode only has Latin-1/CP437
// glyphs burned into firmware. Sending UTF-8 bytes for Arabic, Hindi,
// etc. through text commands prints blanks or garbage — there's no
// glyph to draw. The only reliable fix on generic hardware is to
// rasterize the string with a real font (via canvas) and send it as
// a bitmap, exactly like the QR code above.
// ============================================================

function isAsciiOnly(text: string): boolean {
  return /^[\x00-\x7F]*$/.test(text);
}

// Renders one line of text as a canvas bitmap and sends it as an ESC/POS raster
// image — same technique as the POS bluetoothPrinter.ts. Works for any
// script/language since it's pixels, not printer-decoded bytes.
function rasterizeTextLine(
  text: string,
  opts: { width?: number; fontSize?: number; align?: 'left' | 'center'; bold?: boolean } = {}
): number[] {
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 24;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const fontSpec = `${opts.bold ? 'bold ' : ''}${fontSize}px 'Noto Sans Devanagari','Noto Sans Arabic','Noto Sans',sans-serif`;
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;
  ctx.font = fontSpec; // reapply — resizing canvas resets context state
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';
  const align = opts.align || 'center';
  ctx.textAlign = align === 'center' ? 'center' : 'left';
  const x = align === 'center' ? canvas.width / 2 : 6;
  ctx.fillText(text, x, canvas.height / 2, canvas.width - 12);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x2 = 0; x2 < canvas.width; x2++) {
      const i = (y * canvas.width + x2) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x2 / 8);
        pixels[byteIndex] |= 1 << (7 - (x2 % 8));
      }
    }
  }

  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;

  return [
    ...CMD.ALIGN_CENTER,
    GS, 0x76, 0x30, 0x00, xL, xH, yL, yH,
    ...Array.from(pixels),
    ...CMD.ALIGN_CENTER,
  ];
}

// Draws "label ......... value" on ONE raster line, label left / value right —
// mirrors row()'s layout but as a bitmap, for the translated bilingual row.
function rasterizeLabelValueLine(
  label: string,
  value: string,
  opts: { width?: number; fontSize?: number; bold?: boolean } = {}
): number[] {
  const printerWidthDots = opts.width || 384;
  const fontSize = opts.fontSize || 22;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  const lineHeight = Math.ceil(fontSize * 1.5);
  canvas.width = printerWidthDots;
  canvas.height = lineHeight;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'middle';

  ctx.font = `${fontSize}px 'Noto Sans Devanagari','Noto Sans Arabic','Noto Sans',sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText(label, 6, canvas.height / 2, canvas.width * 0.55);

  ctx.font = `${opts.bold ? 'bold ' : ''}${fontSize}px 'Noto Sans Devanagari','Noto Sans Arabic','Noto Sans',sans-serif`;
  ctx.textAlign = 'right';
  ctx.fillText(value, canvas.width - 6, canvas.height / 2, canvas.width * 0.4);

  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const widthBytes = Math.ceil(canvas.width / 8);
  const pixels = new Uint8Array(widthBytes * canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      if (lum < 200) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        pixels[byteIndex] |= 1 << (7 - (x % 8));
      }
    }
  }
  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = canvas.height & 0xff, yH = (canvas.height >> 8) & 0xff;
  return [
    ...CMD.ALIGN_LEFT,
    GS, 0x76, 0x30, 0x00, xL, xH, yL, yH,
    ...Array.from(pixels),
  ];
}

const ARABIC_INDIC_DIGITS: Record<string, string> = { '0':'٠','1':'١','2':'٢','3':'٣','4':'٤','5':'٥','6':'٦','7':'٧','8':'٨','9':'٩' };
function toArabicIndicDigits(text: string): string {
  return text.replace(/[0-9]/g, d => ARABIC_INDIC_DIGITS[d]);
}

// Fast text bytes for ASCII, raster fallback for any other script — exactly
// the POS strategy. No per-language font/RTL config needed: the canvas
// handles shaping/direction automatically for whatever text is passed in.
function textLineBytes(
  text: string,
  opts: { width?: number; fontSize?: number; align?: 'left' | 'center'; bold?: boolean } = {},
  arabicNumerals?: boolean
): number[] {
  if (!text) return [];
  if (isAsciiOnly(text)) return textToBytes(text + '\n');
  const finalText = arabicNumerals ? toArabicIndicDigits(text) : text;
  return rasterizeTextLine(finalText, opts);
}

// One data field, printed as two lines: the English row via fast text bytes,
// and — if bilingual — a translated row via raster underneath it.
//
// IMPORTANT: `value` is NEVER passed through tr(). Only the label is
// translated by default. Pass `value2` explicitly when you have a REAL
// translation of the value (e.g. translatedName from the Google Translate
// call, or a service string that legitimately has a translation entry).
// If arabicNumerals is true, any digit characters in the translated value
// are auto-swapped to Arabic-Indic digits — safe to call unconditionally,
// since toArabicIndicDigits only touches 0-9 and leaves everything else alone.
function bilingualRow(
  label: string,
  value: string,
  tr: (t: string) => string,
  isBilingual: boolean,
  arabicNumerals: boolean,
  value2?: string,
  width = 32
): number[] {
  if (!value) return [];
  const out = [...row(label, value, width)];
  if (isBilingual) {
    const trLabel = tr(label);
    let trValue = value2 !== undefined ? value2 : value; // never tr() raw data
    if (arabicNumerals) trValue = toArabicIndicDigits(trValue);
    if (trLabel !== label || trValue !== value) {
      out.push(...rasterizeLabelValueLine(trLabel, trValue, { fontSize: 22, bold: true }));
    }
  }
  return out;
}

// ============================================================
// Queue Print Job
// ============================================================

export interface QueuePrintJob {
  queueName?: string;

  tokenNumber: string | number;

  tokenCode?: string;

  customerName?: string;

  customerPhone?: string;

  counterName?: string;

  service?: string;

  appointmentTime?: string;

  walkIn?: boolean;

  trackingUrl?: string;

  reference?: string;

  notes?: string;

  // Bilingual support — labels default to the same English fallbacks
  // translateSlip always includes ('Customer'/'Counter'/'Service'), but
  // pass the real queueData labels so custom label text (e.g. "Patient"
  // instead of "Customer") translates correctly too.
  translations?: Record<string, string>;
  translatedName?: string;
  translatedService?: string; // transliterated service value (sound-based, not /translate)
  translatedNotes?: string;
  localizedDate?: string;
  lang?: string;
  customerLabel?: string;
  counterLabel?: string;
  serviceLabel?: string;
}

// ============================================================
// Build Queue ESC/POS ticket
// ============================================================

export function buildQueuePrintBytes(
  job: QueuePrintJob
): number[] {
  const translations = job.translations || {};
  const tr = (text: string) => translations[text] || text;
  const isBilingual = Object.keys(translations).length > 0;
  const arabicNumerals = job.lang === 'ar';

  const customerLabel = job.customerLabel || 'Customer';
  const counterLabel = job.counterLabel || 'Counter';
  const serviceLabel = job.serviceLabel || 'Service';

  const output: number[] = [
    ...CMD.INIT,

    // ========================================================
    // HEADER
    // ========================================================

    ...CMD.ALIGN_CENTER,

    ...CMD.BOLD_ON,
    ...CMD.DOUBLE_HEIGHT,

    ...textToBytes(
      (job.queueName ||
        'QUEUE MANAGER') +
        '\n'
    ),

    ...CMD.NORMAL_SIZE,
    ...CMD.BOLD_OFF,

    ...(isBilingual && job.queueName && tr(job.queueName) !== job.queueName
      ? textLineBytes(tr(job.queueName), { fontSize: 28, bold: true }, arabicNumerals)
      : []),

    ...textToBytes(
      new Date().toLocaleString(
        'en-IN',
        {
          dateStyle: 'medium',
          timeStyle: 'short',
        }
      ) + '\n'
    ),

    // Secondary localized date line — mirrors the web/PDF path's
    // localizedNow(). job.localizedDate is computed client-side (Intl
    // locale lookup) since the printer module has no locale table of its
    // own; this just renders whatever string it's given.
    ...(isBilingual && job.localizedDate
      ? textLineBytes(job.localizedDate, { fontSize: 22 }, arabicNumerals)
      : []),

    ...divider(),

    // ========================================================
    // TOKEN NUMBER
    // ========================================================

    ...CMD.BOLD_ON,

    ...textToBytes(
      'TOKEN NUMBER\n'
    ),

    ...CMD.BOLD_OFF,

    ...(isBilingual && tr('TOKEN NUMBER') !== 'TOKEN NUMBER'
      ? textLineBytes(tr('TOKEN NUMBER'), { fontSize: 22, bold: true }, arabicNumerals)
      : []),

    ...CMD.BOLD_ON,
    ...CMD.DOUBLE_HEIGHT,

    ...textToBytes(
      String(
        job.tokenNumber
      ).padStart(3, '0') +
        '\n'
    ),

    ...CMD.NORMAL_SIZE,
    ...CMD.BOLD_OFF,

    // ========================================================
    // WALK-IN / APPOINTMENT
    // ========================================================

    ...textToBytes(
      job.walkIn === false
        ? 'APPOINTMENT\n'
        : 'WALK-IN\n'
    ),

    ...(isBilingual
      ? (() => {
          const label = job.walkIn === false ? 'APPOINTMENT' : 'WALK-IN';
          return tr(label) !== label ? textLineBytes(tr(label), { fontSize: 24, bold: true }, arabicNumerals) : [];
        })()
      : []),

    ...divider(),

    // ========================================================
    // CUSTOMER DETAILS
    // ========================================================

    ...CMD.ALIGN_LEFT,

    ...bilingualRow(
      customerLabel, job.customerName || '', tr, isBilingual, arabicNumerals,
      job.translatedName // only field we trust as a real translated value
    ),

    ...bilingualRow(
      'Phone', job.customerPhone || '', tr, isBilingual, arabicNumerals
      // no value2 — phone digits get Arabic-Indic-converted automatically
    ),

    ...bilingualRow(
      counterLabel, job.counterName || '', tr, isBilingual, arabicNumerals
      // no value2 — counter NAME is not a translatable phrase, never tr()'d
    ),

    ...bilingualRow(
      serviceLabel, job.service || '', tr, isBilingual, arabicNumerals,
      job.translatedService // transliterated client-side, same mechanism as customer name
    ),

    ...bilingualRow(
      'Time Slot', job.appointmentTime || '', tr, isBilingual, arabicNumerals
      // no value2 — just digit-converts the time string automatically
    ),

    // ========================================================
    // NOTES
    // ========================================================

    ...(job.notes
      ? [
          ...divider(),
          ...bilingualRow(
            'Notes', job.notes, tr, isBilingual, arabicNumerals,
            job.translatedNotes // transliterated client-side, not a dictionary lookup
          ),
        ]
      : []),

    ...divider(),

    // ========================================================
    // QR
    // ========================================================

    ...CMD.ALIGN_CENTER,

    ...textToBytes(
      'SCAN TO TRACK YOUR TURN\n'
    ),

    ...(isBilingual && tr('SCAN TO TRACK YOUR TURN LIVE') !== 'SCAN TO TRACK YOUR TURN LIVE'
      ? textLineBytes(tr('SCAN TO TRACK YOUR TURN LIVE'), { fontSize: 22, align: 'center' }, arabicNumerals)
      : []),

    ...(job.trackingUrl
      ? [
          ...qrImage(
            job.trackingUrl,
            4
          ),
          ...textToBytes('\n'),
        ]
      : []),

    // ========================================================
    // FOOTER
    // ========================================================

    ...CMD.BOLD_ON,

    ...textToBytes(
      'You will be notified\n' +
      'when your turn arrives.\n'
    ),

    ...CMD.BOLD_OFF,

    ...(isBilingual
      ? textLineBytes(
          tr('You will receive an alarm on your phone when your turn arrives. Keep this slip.'),
          { fontSize: 22, align: 'center' },
          arabicNumerals
        )
      : []),

    // ========================================================
    // REFERENCE
    // ========================================================

    ...(job.reference
      ? [
          ...textToBytes('\n'),

          ...textToBytes(
            `Ref: ${job.reference}\n`
          ),
        ]
      : []),

    // ========================================================
    // FINISH
    // ========================================================

    ...CMD.FEED,

    ...CMD.CUT,
  ];

  return output;
}

// ============================================================
// USB PRINTER
// ============================================================

let queuePrinterDevice: USBDevice | null =
  null;

let queuePrinterInterface = -1;

let queuePrinterEndpoint = -1;

// ============================================================
// Find writable USB OUT endpoint
// ============================================================

function findUsbOutEndpoint(
  device: USBDevice
): {
  interfaceNumber: number;
  endpointNumber: number;
} | null {
  const configurations =
    device.configurations || [];

  for (
    const configuration of configurations
  ) {
    for (
      const iface of
        configuration.interfaces
    ) {
      for (
        const alternate of
          iface.alternates
      ) {
        for (
          const endpoint of
            alternate.endpoints
        ) {
          if (
            endpoint.direction ===
            'out'
          ) {
            return {
              interfaceNumber:
                iface.interfaceNumber,

              endpointNumber:
                endpoint.endpointNumber,
            };
          }
        }
      }
    }
  }

  return null;
}

// ============================================================
// Connect Queue USB printer
// ============================================================

async function connectQueuePrinter(): Promise<boolean> {
  if (
    !('usb' in navigator)
  ) {
    alert(
      'WebUSB is not supported in this browser. Please use Chromium or Chrome.'
    );

    return false;
  }

  try {
    const usb =
      (
        navigator as Navigator & {
          usb: USB;
        }
      ).usb;

    // ========================================================
    // Show USB printer chooser
    // ========================================================

    const device =
      await usb.requestDevice({
        filters: [],
      });

    console.log(
      'Selected USB printer:',
      device.productName
    );

    // ========================================================
    // Open device
    // ========================================================

    await device.open();

    // ========================================================
    // Select first configuration
    // ========================================================

    if (!device.configuration) {
      await device.selectConfiguration(
        1
      );
    }

    // ========================================================
    // Find OUT endpoint
    // ========================================================

    const endpointInfo =
      findUsbOutEndpoint(
        device
      );

    if (!endpointInfo) {
      await device.close();

      alert(
        'USB printer found, but no writable OUT endpoint was found.'
      );

      return false;
    }

    // ========================================================
    // Claim printer interface
    // ========================================================

    try {
      await device.claimInterface(
        endpointInfo.interfaceNumber
      );
    } catch (error) {
      console.error(
        'Could not claim USB printer interface:',
        error
      );

      try {
        await device.close();
      } catch {
        // Ignore close error.
      }

      alert(
        'Could not access the printer USB interface.'
      );

      return false;
    }

    // ========================================================
    // Store connection
    // ========================================================

    queuePrinterDevice =
      device;

    queuePrinterInterface =
      endpointInfo.interfaceNumber;

    queuePrinterEndpoint =
      endpointInfo.endpointNumber;

    console.log(
      '✅ Queue USB printer connected'
    );

    console.log(
      'Printer:',
      device.productName
    );

    console.log(
      'Vendor ID:',
      device.vendorId
    );

    console.log(
      'Product ID:',
      device.productId
    );

    console.log(
      'Interface:',
      queuePrinterInterface
    );

    console.log(
      'Endpoint:',
      queuePrinterEndpoint
    );

    return true;

  } catch (error: any) {

    if (
      error?.name !==
      'NotFoundError'
    ) {
      console.error(
        'Queue USB printer connection failed:',
        error
      );
    }

    return false;
  }
}

// ============================================================
// Print Queue Ticket through USB
// ============================================================

export async function printQueueTicket(
  job: QueuePrintJob
): Promise<boolean> {

  try {

    let device =
      queuePrinterDevice;

    // ========================================================
    // Connect if not already connected
    // ========================================================

    if (
      !device ||
      !device.opened
    ) {
      const connected =
        await connectQueuePrinter();

      if (!connected) {
        return false;
      }

      device =
        queuePrinterDevice;
    }

    if (!device) {
      return false;
    }

    // ========================================================
    // Build ESC/POS bytes
    // ========================================================

    const bytes =
      buildQueuePrintBytes(
        job
      );

    console.log(
      '🖨️ Queue print bytes:',
      bytes.length
    );

    // ========================================================
    // Send in small chunks
    // ========================================================

    const chunkSize = 64;

    for (
      let i = 0;
      i < bytes.length;
      i += chunkSize
    ) {

      const chunk =
        bytes.slice(
          i,
          i + chunkSize
        );

      const result =
        await device.transferOut(
          queuePrinterEndpoint,
          new Uint8Array(
            chunk
          )
        );

      if (
        result.status !==
        'ok'
      ) {
        throw new Error(
          `USB transfer failed: ${result.status}`
        );
      }

      // Small delay prevents some
      // thermal printers from dropping
      // packets.
      await new Promise(
        resolve =>
          setTimeout(
            resolve,
            10
          )
      );
    }

    console.log(
      '✅ Queue ticket printed successfully via USB'
    );

    return true;

  } catch (error) {

    console.error(
      '❌ Queue USB printer failed:',
      error
    );

    // ========================================================
    // Reset connection
    // ========================================================

    try {

      if (
        queuePrinterDevice?.opened
      ) {
        await queuePrinterDevice.close();
      }

    } catch {
      // Ignore close errors.
    }

    queuePrinterDevice =
      null;

    queuePrinterInterface =
      -1;

    queuePrinterEndpoint =
      -1;

    return false;
  }
}